import {
  Button,
  HStack,
  IconButton,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useClipboard,
  useToast,
  VStack,
} from '@chakra-ui/react';
import ContentCopySharpIcon from '@gamma/ui/icons/ContentCopySharpIcon';
import DashboardSharpIcon from '@gamma/ui/icons/DashboardSharpIcon';
import DatabaseSharpIcon from '@gamma/ui/icons/DatabaseSharpIcon';
import FrameInspectSharpIcon from '@gamma/ui/icons/FrameInspectSharpIcon';
import KeyboardArrowDownSharpIcon from '@gamma/ui/icons/KeyboardArrowDownSharpIcon';
import ListAltSharpIcon from '@gamma/ui/icons/ListAltSharpIcon';
import MoreHorizSharpIcon from '@gamma/ui/icons/MoreHorizSharpIcon';
import NorthEastSharpIcon from '@gamma/ui/icons/NorthEastSharpIcon';
import { RouterLink } from '@gamma/ui/navigation';
import { useContext, useEffect, useMemo } from 'react';
import {
  ROUTES,
  URLS,
  EXTERNAL_SERVICE_LOGOS,
} from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import { useEntitiesContext } from '../../context';
import { SuppressEntityMenuItem } from '@investigator/shared/components/SuppressEntityMenuItem';
import { logScaleEntityPivot } from '@investigator/shared/utils';
import { isPublicIPAddress } from '../../utils';
import { ExternalIPLinksMenu } from '../ExternalIPLinksMenu';

const { CENSYS, SHODAN, VIRUS_TOTAL } = URLS;
const {
  VIRUS_TOTAL: VIRUS_TOTAL_LOGO,
  SHODAN: SHODAN_LOGO,
  CENSYS: CENSYS_LOGO,
} = EXTERNAL_SERVICE_LOGOS;

const { copied, copyValue, controls, viewDetails, viewDetections } =
  i18n.pages.detections;

export interface EntityActionsMenuProps {
  isModal?: boolean;
  onClose?: () => void;
}

export const EntityActionsMenu = ({
  isModal,
  onClose,
}: EntityActionsMenuProps) => {
  const toast = useToast();
  const { userRole } = useContext(AuthContext);
  const {
    selectedEntity,
    analysisModal: { onOpen: onEntityModalOpen },
    suppressionModal: { onOpen: onSuppressOpen },
    suppression,
  } = useEntitiesContext();

  // Get entity data from context
  const entityName = selectedEntity?.entityName || '';
  const entityCategory = selectedEntity?.entityCategory || 'source';
  const start = selectedEntity?.start;
  const end = selectedEntity?.end;

  const { hasCopied, onCopy } = useClipboard(entityName);

  // Derive suppression state for UI from the new structure
  const isSuppressed = selectedEntity?.entityIsSuppressed || false;
  const isLoading = suppression.loading || suppression.pendingUpdate;
  const isButtonDisabled = !suppression.enabled;

  useEffect(() => {
    if (hasCopied) {
      toast({
        position: 'bottom-right',
        status: 'success',
        description: copied,
      });
    }
  }, [hasCopied, toast]);

  const logScaleEntityQuery = useMemo(
    () =>
      start && end && entityName
        ? logScaleEntityPivot({
            start,
            end,
            entityName,
          })
        : '',
    [end, entityName, start],
  );

  return (
    <VStack pb={!isModal ? 2 : 0} spacing={4}>
      <HStack w="100%">
        <Menu>
          <MenuButton
            as={Button}
            flexGrow={1}
            variant="solid"
            colorScheme="blue"
            size="sm"
          >
            <HStack ml={!isModal ? '102px' : 0}>
              <FrameInspectSharpIcon />
              <Text>{controls.investigate}</Text>
              <KeyboardArrowDownSharpIcon />
            </HStack>
          </MenuButton>
          <MenuList>
            {!isModal && (
              <MenuItem
                as={Link}
                _hover={{
                  textDecoration: 'none',
                }}
                onClick={() => onEntityModalOpen(selectedEntity)}
              >
                <HStack>
                  <DashboardSharpIcon />
                  <Text>{viewDetails}</Text>
                </HStack>
              </MenuItem>
            )}
            <MenuItem
              minW={!isModal ? '335px' : 'auto'}
              target="_blank"
              as={RouterLink}
              icon={<DatabaseSharpIcon />}
              to={{
                pathname: ROUTES.logSearch,
                search: logScaleEntityQuery,
              }}
            >
              <HStack>
                <Text>{controls.viewIpLogs}</Text>
                <NorthEastSharpIcon />
              </HStack>
            </MenuItem>
            <MenuItem
              as={RouterLink}
              onClick={onClose}
              icon={<ListAltSharpIcon />}
              to={{
                pathname: ROUTES.detections,
                search: `start=${start}&end=${end}&${entityCategory}_name=${entityName}`,
              }}
            >
              {viewDetections}
            </MenuItem>
          </MenuList>
        </Menu>
        <Menu offset={[isModal ? -192 : -199, 8]}>
          <Tooltip label={controls.moreActions}>
            <MenuButton
              as={IconButton}
              variant="solid"
              colorScheme="gray"
              isLoading={isLoading}
              size="sm"
            >
              <HStack justifyContent="center">
                <MoreHorizSharpIcon />
              </HStack>
            </MenuButton>
          </Tooltip>
          <MenuList>
            {userRole !== 'viewer' && onSuppressOpen && (
              <SuppressEntityMenuItem
                isSuppressed={isSuppressed}
                isLoading={isLoading}
                isDisabled={isButtonDisabled}
                onClick={onSuppressOpen}
                suppressText={controls.suppressEntity}
                unsuppressText={controls.unSuppressEntity}
              />
            )}
            <MenuItem onClick={onCopy} icon={<ContentCopySharpIcon />}>
              {copyValue}
            </MenuItem>
            {isModal && entityName && isPublicIPAddress(entityName) && (
              <ExternalIPLinksMenu
                entityName={entityName}
                useMenuGroup={false}
              />
            )}
          </MenuList>
        </Menu>
      </HStack>
      {!isModal && entityName && isPublicIPAddress(entityName) && (
        <HStack w="100%">
          <Button
            as={Link}
            isExternal
            flexGrow={1}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            textAlign="center"
            href={`${VIRUS_TOTAL}/${entityName}`}
            css={{ textDecoration: 'none !important' }}
          >
            <HStack>
              <Image w="13px" h="13px" src={VIRUS_TOTAL_LOGO} />
              <Text color="text.primary">VirusTotal</Text>
              <NorthEastSharpIcon />
            </HStack>
          </Button>
          <Button
            as={Link}
            isExternal
            flexGrow={1}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            textAlign="center"
            href={`${SHODAN}/${entityName}`}
            css={{ textDecoration: 'none !important' }}
          >
            <HStack>
              <Image w="16px" h="16px" src={SHODAN_LOGO} />
              <Text color="text.primary">Shodan</Text>
              <NorthEastSharpIcon />
            </HStack>
          </Button>
          <Button
            as={Link}
            isExternal
            flexGrow={1}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            textAlign="center"
            href={`${CENSYS}/${entityName}`}
            css={{ textDecoration: 'none !important' }}
          >
            <HStack>
              <Image w="16px" h="16px" src={CENSYS_LOGO} />
              <Text color="text.primary">Censys</Text>
              <NorthEastSharpIcon />
            </HStack>
          </Button>
        </HStack>
      )}
    </VStack>
  );
};
